/**
 * Tacit CRM React Query Hooks
 * TanStack Query hooks for contact management
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { crmApi, type ListContactsFilters } from '@/lib/crm/api';
import { supabase } from '@/lib/supabase';
import type { Tables } from '@/types/database';
import { createDashboardCounts, getDashboardLane, type DashboardLane } from '@/lib/crm/dashboard';
import { dueWindowFor } from '@/lib/crm/query-filters';
import { buildLoggedReply, type LogReplyInput } from '@/lib/crm/log-reply';
import type { CrmFilters } from '@/hooks/crm/useCrmFilters';
import type { Contact, ContactFilters, AIProcessResponse, AIUpdateResponse, AIFieldUpdate } from '@/lib/crm/types';

export type CrmContactRecord = Tables<'crm_contacts'>;
/** Exported so sibling CRM hooks scope their cache the same way, not a second way. */
export type CrmQueryScope = readonly [userId: string, organizationId: string];

/** The filters a lane-scoped view (`useLaneContacts`) accepts — everything from `useCrmFilters` except the lane itself, which is passed as a separate argument. */
export type LaneContactFilters = Omit<CrmFilters, 'lane'>;

const UNRESOLVED_CRM_SCOPE = ['unresolved-user', 'unresolved-organization'] as const;

const DEFAULT_LANE_PAGE_SIZE = 20;

export function useCrmQueryScope(): CrmQueryScope | null {
  const { user, organizationId } = useAuth();

  return user?.id && organizationId
    ? [user.id, organizationId]
    : null;
}

export function resolvedScopeKey(scope: CrmQueryScope | null): CrmQueryScope {
  return scope ?? UNRESOLVED_CRM_SCOPE;
}

// ═══════════════════════════════════════════════════════════════════════════
// QUERY KEY FACTORY
// ═══════════════════════════════════════════════════════════════════════════

export const crmKeys = {
  all: ['crm'] as const,
  contacts: () => [...crmKeys.all, 'contacts'] as const,
  contactsList: (scope: CrmQueryScope, filters: ContactFilters) =>
    [...crmKeys.contacts(), 'list', ...scope, filters] as const,
  contactsIndex: (scope: CrmQueryScope) =>
    [...crmKeys.contacts(), 'index', ...scope] as const,
  contactsLane: (scope: CrmQueryScope, lane: DashboardLane, filters: LaneContactFilters) =>
    [...crmKeys.contacts(), 'lane', ...scope, lane, filters] as const,
  contactsToday: (scope: CrmQueryScope) =>
    [...crmKeys.contacts(), 'today', ...scope] as const,
  /**
   * `todayIso` is part of the key (not just user/org scope) so a remount
   * that crosses local midnight forces a fresh fetch — see
   * `useDueScheduledContacts`.
   */
  contactsDueScheduled: (scope: CrmQueryScope, todayIso: string) =>
    [...crmKeys.contacts(), 'due-scheduled', ...scope, todayIso] as const,
  contact: (scope: CrmQueryScope, id: string) =>
    [...crmKeys.contacts(), 'detail', ...scope, id] as const,
  activities: () => [...crmKeys.all, 'activities'] as const,
  activityList: (scope: CrmQueryScope, contactId: string) =>
    [...crmKeys.activities(), 'list', ...scope, contactId] as const,
};

// ═══════════════════════════════════════════════════════════════════════════
// QUERY HOOKS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fetch contacts with optional filters
 */
export function useContacts(filters: ContactFilters = {}) {
  const scope = useCrmQueryScope();

  return useQuery({
    queryKey: crmKeys.contactsList(resolvedScopeKey(scope), filters),
    queryFn: () => crmApi.listContacts(scope![1], filters),
    enabled: scope !== null,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch a single contact by ID
 */
export function useContact(id: string | undefined) {
  const scope = useCrmQueryScope();

  return useQuery<CrmContactRecord>({
    queryKey: crmKeys.contact(resolvedScopeKey(scope), id || ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('organization_id', scope![1])
        .eq('id', id!)
        .single();

      if (error) throw error;
      return data as CrmContactRecord;
    },
    enabled: !!id && scope !== null,
    retry: false,
  });
}

/**
 * Fetch activities for a contact
 */
export function useActivities(contactId: string | undefined) {
  const scope = useCrmQueryScope();

  return useQuery({
    queryKey: crmKeys.activityList(resolvedScopeKey(scope), contactId || ''),
    queryFn: () => crmApi.getActivities(scope![1], contactId!),
    enabled: !!contactId && scope !== null,
  });
}

/**
 * Translates a `CrmFilters.due` value into the `ListContactsFilters` window
 * params via the shared `dueWindowFor` helper (see `query-filters.ts`) —
 * this is the only place that bridges the dashboard's `DashboardFilter`
 * vocabulary to the API's raw `dueOn`/`dueFrom`/`dueBefore` fields, so the
 * two never drift independently.
 */
function buildLaneApiFilters(
  lane: DashboardLane,
  filters: LaneContactFilters,
  now: Date,
): ListContactsFilters {
  const offset = (filters.page - 1) * DEFAULT_LANE_PAGE_SIZE;
  const apiFilters: ListContactsFilters = {
    lane,
    limit: DEFAULT_LANE_PAGE_SIZE,
    offset,
  };

  if (filters.q) {
    apiFilters.search = filters.q;
  }

  switch (filters.due) {
    case 'overdue':
      apiFilters.overdue = true;
      break;
    case 'due-today': {
      const window = dueWindowFor('due-today', now);
      if (window?.from) apiFilters.dueOn = window.from;
      break;
    }
    case 'due-this-week': {
      const window = dueWindowFor('due-this-week', now);
      if (window?.from) apiFilters.dueFrom = window.from;
      if (window?.to) apiFilters.dueBefore = window.to;
      break;
    }
    case 'total-active':
    case null:
      break;
  }

  return apiFilters;
}

/**
 * Counts for the dashboard's stat tiles and lane badges. Backed by the
 * lightweight 6-column `ContactIndexRow` projection (`listContactIndex`),
 * not full contact rows, so a frequent badge refresh stays cheap.
 */
export function useDashboardCounts() {
  const scope = useCrmQueryScope();

  return useQuery({
    queryKey: crmKeys.contactsIndex(resolvedScopeKey(scope)),
    queryFn: async () => {
      const index = await crmApi.listContactIndex(scope![1]);
      return createDashboardCounts(index, new Date());
    },
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

/**
 * Paged contacts for a single dashboard lane. The server-side `lane` filter
 * is a safe superset (see the invariant in `dashboard.ts`) — lane membership
 * itself is not re-derived here, it's just what the server was asked for.
 */
export function useLaneContacts(
  lane: DashboardLane,
  filters: LaneContactFilters,
  options: { enabled?: boolean } = {},
) {
  const scope = useCrmQueryScope();

  return useQuery({
    queryKey: crmKeys.contactsLane(resolvedScopeKey(scope), lane, filters),
    queryFn: () => crmApi.listContacts(scope![1], buildLaneApiFilters(lane, filters, new Date())),
    enabled: scope !== null && (options.enabled ?? true),
    // Keeps the previous page's rows on screen while the next page loads,
    // instead of flashing to a loading state — the invariant this hooks
    // into pagination for.
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
  });
}

/**
 * The "what needs doing today" queue: my-turn contacts due today. Their-turn/
 * scheduled/nurture contacts due today are surfaced in their own lanes, not
 * here — this is specifically the chase queue (see the Wave 1a taxonomy
 * notes in dashboard.test.ts: my-turn is "the only rule that produces daily
 * work").
 */
export function useTodayQueue() {
  const scope = useCrmQueryScope();

  return useQuery({
    queryKey: crmKeys.contactsToday(resolvedScopeKey(scope)),
    queryFn: () => {
      const now = new Date();
      const todayIso = dueWindowFor('due-today', now)?.from;

      return crmApi.listContacts(scope![1], {
        lane: 'my-turn',
        dueOn: todayIso,
        orderBy: { column: 'due_date', ascending: true },
      });
    },
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

// PostgREST's server-side `max-rows` (default 1,000 — see
// src/lib/supabase-pagination.ts:21, and the 5e310c7 incident: a query
// without an explicit smaller limit was truncated at exactly 1,000 rows in
// production, not 500) sits well above 501, so this sentinel is not at risk
// of being silently down-capped by the server itself.
const DUE_SCHEDULED_FETCH_LIMIT = 501;

// Kept in one place so the `select()` string can't drift from the columns
// `getDashboardLane`/`selectDueScheduledContacts` and the render actually
// need.
const DUE_SCHEDULED_COLUMNS =
  'id, name, company, due_date, status, action_state, completed_at';

function toLocalIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Scheduled contacts whose `due_date` has already arrived ("due now"),
 * bounded by `DUE_SCHEDULED_FETCH_LIMIT` — a completeness SENTINEL, not a
 * display cap and not a claim that "currently scheduled and due" is bounded
 * under that number (this repo's own `5e310c7` commit dealt with 1,513
 * contacts). Fewer than `DUE_SCHEDULED_FETCH_LIMIT` rows coming back PROVES
 * the fetch captured the entire due-scheduled superset for today, so
 * `selectDueScheduledContacts`'s filtered output is the exact, complete
 * population — see `isDueScheduledListComplete`, which turns this into a
 * boolean callers can act on. Exactly `DUE_SCHEDULED_FETCH_LIMIT` rows means
 * the true count is unknown and must be presented as such — independent of
 * how many rows survive the ladder filter — via a non-numeric incompleteness
 * note (`TodayQueue.tsx` renders "Scheduled due list may be incomplete"
 * rather than a specific count in that case). The exact, ladder-accurate
 * membership and its display cap are derived afterwards, by
 * `selectDueScheduledContacts` and `TodayQueue.tsx`.
 *
 * The query key includes the local ISO date (not just user/org scope) —
 * `staleTime` alone does not guarantee a fresh fetch on a remount that
 * lands inside the 30s window across local midnight (e.g. fetched at
 * 23:59:50, remounted at 00:00:05 would otherwise serve yesterday's
 * cached, date-scoped result). Including the date forces a fresh fetch
 * whenever the local date changes, with no live clock or timer involved.
 * **The key alone only covers a REMOUNT** — a `refetch()` on an already-
 * mounted query (e.g. an error state's Retry button) re-runs the same
 * `queryFn` closure without a parent re-render in between, so the actual
 * `dueBefore` value sent to the server is computed fresh from `new Date()`
 * inside `queryFn` itself, not from this hook's `now`/`todayIso` closure
 * (DIFF Round 6, F1 — see `queryFn` below).
 *
 * `now` is a REQUIRED parameter, not computed internally — `TodayQueue.tsx`
 * and `DashboardSections.tsx` both call this hook independently, and if each
 * computed its own `new Date()` at call time, the two renders could straddle
 * local midnight and disagree on the query key: Today might still read
 * yesterday's cached result while the SCHEDULED exclusion set has already
 * moved to today's, silently vanishing a newly-due contact from both
 * surfaces — the exact bug class this whole feature exists to prevent, via
 * a narrower race window. Every caller must pass the SAME `now` snapshot,
 * taken once in the shared ancestor (`src/pages/crm/index.tsx`) — matching
 * how `partitionDashboardContacts`/`getDashboardLane` already take an
 * explicit `now` rather than computing their own.
 */
export function useDueScheduledContacts(now: Date) {
  const scope = useCrmQueryScope();
  const todayIso = toLocalIsoDate(now);

  return useQuery({
    queryKey: crmKeys.contactsDueScheduled(resolvedScopeKey(scope), todayIso),
    // `dueBefore` is computed FRESH here, at actual fetch time, from
    // `new Date()` — NOT from the closed-over `todayIso` above. `todayIso`
    // still drives the query KEY (needed for the remount-across-midnight
    // cache-bust this hook's own doc comment describes), but a user-
    // triggered `refetch()` (e.g. the error state's Retry button in
    // `TodayQueue.tsx`) re-runs this SAME `queryFn` closure without going
    // through a parent re-render first — if `dueBefore` reused the stale
    // `todayIso` closure, a Retry clicked after local midnight would still
    // fetch yesterday's date boundary and could false-green "Nothing due
    // today" (DIFF Round 6, F1). Recomputing here makes every actual fetch
    // date-correct regardless of how it was triggered.
    queryFn: () =>
      crmApi.listContacts(scope![1], {
        lane: 'scheduled',
        dueBefore: toLocalIsoDate(new Date()),
        select: DUE_SCHEDULED_COLUMNS,
        limit: DUE_SCHEDULED_FETCH_LIMIT,
        orderBy: { column: 'due_date', ascending: true },
      }),
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

/**
 * Re-applies the exact ladder predicate (`getDashboardLane`) over the full
 * backstop-bounded fetch from `useDueScheduledContacts` — the server-side
 * `lane: 'scheduled'` filter is only a safe superset on `action_state`
 * (see the architectural invariant in `dashboard.ts`), so an
 * inactive-but-scheduled row (e.g. `status='closed_lost'`,
 * `action_state='scheduled'`) must still be excluded here.
 *
 * Returns `[]` on `isError`, and also while still loading with no prior
 * data — this selector never derives membership from stale `data` while
 * the query that would render it is showing an error.
 */
export function selectDueScheduledContacts(
  queryResult: Pick<UseQueryResult<Contact[]>, 'data' | 'isError'>,
  now: Date,
): Contact[] {
  if (queryResult.isError) return [];
  if (!queryResult.data) return [];
  return queryResult.data.filter((contact) => getDashboardLane(contact, now) === 'scheduled');
}

/**
 * `true` when the raw fetch behind `useDueScheduledContacts` is provably
 * exhaustive for today's window (fewer than `DUE_SCHEDULED_FETCH_LIMIT`
 * rows came back) — i.e. `selectDueScheduledContacts`'s filtered output is
 * the exact, complete due-scheduled population, not a possibly-truncated
 * sample. `false` when the fetch hit its cap and the true count is
 * unknown (regardless of how many of those 501 rows pass the ladder
 * filter — hitting the raw cap means we can't rule out more valid rows
 * existing beyond what was fetched).
 */
export function isDueScheduledListComplete(
  queryResult: Pick<UseQueryResult<Contact[]>, 'data'>,
): boolean {
  return queryResult.data !== undefined && queryResult.data.length < DUE_SCHEDULED_FETCH_LIMIT;
}

/**
 * `true` ONLY when a RESOLVED fetch behind `useDueScheduledContacts` is
 * provably incomplete (hit `DUE_SCHEDULED_FETCH_LIMIT`) — the positive
 * counterpart to `isDueScheduledListComplete`, but deliberately NOT its
 * logical negation. `!isDueScheduledListComplete(...)` is also `true` for a
 * query that simply hasn't resolved yet (`data === undefined` — e.g. still
 * `enabled: false` while `useCrmQueryScope()` resolves), and `TodayQueue.tsx`
 * previously used that negation to gate its "may be incomplete" warning,
 * which meant the warning could render before the fetch had even run. This
 * function stays `false` in that same not-yet-resolved case (`isSuccess`
 * false), `false` once resolved under the cap, and `true` only once resolved
 * AT the cap — the three states the warning needs to tell apart (F11).
 */
export function isDueScheduledListDefinitelyIncomplete(
  queryResult: Pick<UseQueryResult<Contact[]>, 'data' | 'isSuccess'>,
): boolean {
  return queryResult.isSuccess && (queryResult.data?.length ?? 0) >= DUE_SCHEDULED_FETCH_LIMIT;
}

// ═══════════════════════════════════════════════════════════════════════════
// OPTIMISTIC CACHE HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function isRecordWithId(value: unknown): value is { id: unknown } & Record<string, unknown> {
  return typeof value === 'object' && value !== null && 'id' in value;
}

/**
 * Patches every cached shape a contact can appear under — the detail query
 * (a single object), and every list/lane/index query (an array) — with the
 * same partial update, keyed by `id`. A due_date edit can move a contact to
 * a different lane or page; this patches the row IN PLACE and never
 * re-sorts or re-buckets client-side. `onSettled` always invalidates
 * afterwards so the server (and dashboard.ts, the sole lane/bucket
 * authority) can relocate the row on the next fetch.
 */
function patchCachedContactData(
  data: unknown,
  contactId: string,
  patch: Partial<Contact>,
): unknown {
  if (data === undefined) return data;

  if (Array.isArray(data)) {
    return data.map((row) => (
      isRecordWithId(row) && row.id === contactId ? { ...row, ...patch } : row
    ));
  }

  if (isRecordWithId(data) && data.id === contactId) {
    return { ...data, ...patch };
  }

  return data;
}

interface OptimisticContactsContext {
  previous: Array<[QueryKey, unknown]>;
}

function snapshotAndPatchContacts(
  queryClient: QueryClient,
  contactId: string,
  patch: Partial<Contact>,
): OptimisticContactsContext {
  const previous = queryClient.getQueriesData({ queryKey: crmKeys.contacts() });

  queryClient.setQueriesData<unknown>(
    { queryKey: crmKeys.contacts() },
    (data: unknown) => patchCachedContactData(data, contactId, patch),
  );

  return { previous };
}

function restoreContactsSnapshot(
  queryClient: QueryClient,
  context: OptimisticContactsContext | undefined,
): void {
  context?.previous.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detect which fields changed between original and updated contact
 */
function detectChanges(original: Contact, updates: Partial<Contact>): string[] {
  const trackableFields = [
    'name', 'company', 'role', 'email', 'phone', 'linkedin_url',
    'status', 'pain_point', 'next_action', 'strategy', 'due_date', 'notes'
  ];
  
  return trackableFields.filter(field => {
    const key = field as keyof Contact;
    const originalValue = original[key] ?? '';
    const newValue = updates[key] ?? '';
    return updates[key] !== undefined && newValue !== originalValue;
  });
}

/**
 * Get previous values for changed fields
 */
function getPreviousValues(original: Contact, changedFields: string[]): Record<string, any> {
  const result: Record<string, any> = {};
  changedFields.forEach(field => {
    result[field] = original[field as keyof Contact];
  });
  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// MUTATION HOOKS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a new contact
 */
export function useCreateContact() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (contact: Partial<Contact>) => {
      const result = await crmApi.createContact(contact);
      
      // Auto-log contact creation
      try {
        await crmApi.logActivity(result.id, 'note_added', 'Contact created', {
          action: 'created',
          initialData: {
            name: contact.name,
            company: contact.company,
            status: contact.status,
          }
        });
      } catch (logError) {
        console.error('Failed to log activity:', logError);
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
      toast.success('Contact created successfully');
    },
    onError: (error: Error) => {
      console.error('Create contact error:', error);
      toast.error('Failed to create contact');
    },
  });
}

/**
 * Update an existing contact
 */
export function useUpdateContact() {
  const queryClient = useQueryClient();
  const scope = useCrmQueryScope();

  return useMutation({
    mutationFn: async ({ id, updates, originalContact }: {
      id: string;
      updates: Partial<Contact>;
      originalContact?: Contact;
    }) => {
      if (!scope) throw new Error('No organization found for user');

      const result = await crmApi.updateContact(id, updates, scope[1]);

      // Auto-log changes if original contact provided
      if (originalContact) {
        try {
          const changedFields = detectChanges(originalContact, updates);

          if (changedFields.length > 0) {
            const previousValues = getPreviousValues(originalContact, changedFields);
            const fieldLabels: Record<string, string> = {
              name: 'Name',
              company: 'Company',
              role: 'Role',
              email: 'Email',
              phone: 'Phone',
              linkedin_url: 'LinkedIn',
              status: 'Status',
              pain_point: 'Pain Point',
              next_action: 'Next Action',
              strategy: 'Strategy',
              due_date: 'Due Date',
              notes: 'Notes',
            };

            const changedLabels = changedFields.map(f => fieldLabels[f] || f);

            await crmApi.logActivity(id, 'note_added', `Updated: ${changedLabels.join(', ')}`, {
              action: 'updated',
              changedFields,
              previousValues,
              newValues: Object.fromEntries(
                changedFields.map(f => [f, updates[f as keyof Contact]])
              ),
            });
          }
        } catch (logError) {
          console.error('Failed to log activity:', logError);
        }
      }

      return result;
    },
    onMutate: async ({ id, updates }: {
      id: string;
      updates: Partial<Contact>;
      originalContact?: Contact;
    }) => {
      await queryClient.cancelQueries({ queryKey: crmKeys.contacts() });
      return snapshotAndPatchContacts(queryClient, id, updates);
    },
    onError: (error: Error, _variables, context: OptimisticContactsContext | undefined) => {
      restoreContactsSnapshot(queryClient, context);
      console.error('Update contact error:', error);
      toast.error('Failed to update contact');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
      toast.success('Contact updated successfully');
    },
    onSettled: () => {
      // A due_date edit can move a contact to a different lane or page.
      // That relocation is NOT computed client-side — no re-sort, no
      // re-bucket — this just invalidates so the next fetch asks the
      // server again, and dashboard.ts decides where the row belongs.
      queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
    },
  });
}

/**
 * Delete a contact
 */
export function useDeleteContact() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => crmApi.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
      toast.success('Contact deleted successfully');
    },
    onError: (error: Error) => {
      console.error('Delete contact error:', error);
      toast.error('Failed to delete contact');
    },
  });
}

/**
 * Mark a contact as done
 */
export function useMarkDone() {
  const queryClient = useQueryClient();
  const scope = useCrmQueryScope();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!scope) throw new Error('No organization found for user');

      const result = await crmApi.markDone(id, scope[1]);

      // The timeline previously had no record of a contact being marked
      // done — log it so the activity feed isn't missing this milestone.
      try {
        await crmApi.logActivity(id, 'status_changed', 'Marked as done', {
          action: 'marked_done',
        });
      } catch (logError) {
        console.error('Failed to log activity:', logError);
      }

      return result;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: crmKeys.contacts() });
      return snapshotAndPatchContacts(queryClient, id, {
        completed_at: new Date().toISOString(),
        action_state: 'archived',
      });
    },
    onError: (error: Error, _id, context: OptimisticContactsContext | undefined) => {
      restoreContactsSnapshot(queryClient, context);
      console.error('Mark done error:', error);
      toast.error('Failed to mark as done');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
      toast.success('Marked as done');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
    },
  });
}

/**
 * Process raw text with AI
 */
export function useAIProcess() {
  return useMutation({
    mutationFn: (rawText: string) => crmApi.processWithAI(rawText),
    onError: (error: Error) => {
      console.error('AI processing error:', error);
      toast.error('AI processing failed. Please try again.');
    },
  });
}

// Mirrors `useLeadInbox.ts`/`useOutreach.ts`'s own copy of this helper — both
// surfaces authenticate the same way on purpose, duplicated rather than
// shared (see those files' own comment on why a second auth idiom is how the
// LinkedIn analytics 401 happened).
const accessToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
};

/**
 * AI Update - Smart contact matching and data extraction
 * Powers the Quick Update Card feature
 */
export function useAIUpdate() {
  return useMutation({
    mutationFn: async (payload: { rawText: string; contactId?: string }) => {
      const token = await accessToken();
      const response = await fetch('/api/crm/ai-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          raw_text: payload.rawText,
          contact_id: payload.contactId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // `errorData.error` can be an object with no `message` field (e.g.
        // `{code: 'X'}`) — that object is truthy, so `??` alone would use it
        // as-is and stringify to "[object Object]" (F12). Only fall through
        // to it when it's actually a string.
        throw new Error(
          errorData.error?.message
          ?? (typeof errorData.error === 'string' ? errorData.error : 'AI update failed'),
        );
      }

      const result = await response.json();
      return result.data as AIUpdateResponse;
    },
    onError: (error: Error) => {
      console.error('AI update error:', error);
      toast.error('AI update failed. Please try again.');
    },
  });
}

/**
 * Log an activity
 */
export function useLogActivity() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      contactId, 
      activityType, 
      description, 
      metadata 
    }: { 
      contactId: string; 
      activityType: string; 
      description?: string; 
      metadata?: Record<string, any>;
    }) => crmApi.logActivity(contactId, activityType, description, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
    },
  });
}

/**
 * Records a reply the operator sent themselves, and hands the turn back.
 *
 * Two writes, activity first. If the contact patch then fails, the worst case
 * is a logged reply whose contact still reads "you owe a response" — visible,
 * and fixed by retrying. The other order would move the turn with no record of
 * why, which is the state this whole feature exists to eliminate.
 */
export function useLogReply() {
  const queryClient = useQueryClient();
  const scope = useCrmQueryScope();

  return useMutation({
    mutationFn: async ({ contact, input }: { contact: Contact; input: LogReplyInput }) => {
      if (!scope) throw new Error('No organization found for user');

      const { activity, contactUpdates } = buildLoggedReply(input);

      await crmApi.logActivity(
        contact.id,
        activity.activityType,
        activity.description,
        activity.metadata,
      );

      return crmApi.updateContact(contact.id, contactUpdates, scope[1]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
      await queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type { Contact, ContactFilters, AIProcessResponse, AIUpdateResponse, AIFieldUpdate };
